/**
 * The game's voice.
 *
 * Every sound in here is synthesised. Not for purity — because a shooter with
 * four hundred and fifty guns cannot ship four hundred and fifty recordings to
 * a phone, and because a gun's sound should come from the same numbers as its
 * damage. A heavy slow weapon is low and long and a light fast one is sharp
 * and short, and that falls out of the stats rather than out of a folder of
 * files somebody has to keep in step with them.
 *
 * A gunshot is three things layered: a crack, which is the supersonic snap and
 * is almost all high-frequency noise; a body, which is the muzzle blast and is
 * a pitched thump that falls as it decays; and a tail, which is the room
 * answering. Change the proportions and you have every gun in the game.
 *
 * Everything is positioned by ear rather than by `PannerNode`: pan and gain
 * from the listener's own basis, which is a multiply instead of an HRTF
 * convolution and is indistinguishable through a phone speaker.
 *
 * Nothing here can start until the player touches the screen — browsers will
 * not allow it, and rightly. The context is created on the first input and
 * every call before that is a no-op.
 */

import type { Weapon } from './weapons.ts';
import type { EnemyType } from './enemies.ts';

/** Where the ears are. */
interface Listener {
  x: number;
  y: number;
  z: number;
  /** Forward and right, in the XZ plane. */
  fx: number;
  fz: number;
  rx: number;
  rz: number;
}

/** Beyond this, nothing is worth a voice. */
const EARSHOT = 46;
/** More than this at once is mud, and on a phone it is mud that costs frames. */
const MAX_VOICES = 22;

/**
 * What each kind of gun sounds like.
 *
 * The shot used to be one synth scaled by damage, so every weapon in the game
 * was the same event at a different pitch — a machine pistol and a battle
 * rifle differed by a number and by nothing you could name. These are the
 * handful of parameters that separate the families by ear: how low the body
 * sits, what shape it is, how bright and how sharp the crack is, how long the
 * room answers for, and whether there is a second body an octave down.
 *
 * `pitch` multiplies the body frequency, `sweep` is how far above the landing
 * note it starts, `wave` is the oscillator, `crack` scales the highpass corner,
 * `snap` its level, `tail` the length of the room, `thump` a sub an octave
 * down, `length` the whole envelope and `level` the overall loudness.
 */
interface Voice {
  pitch: number; sweep: number; wave: OscillatorType;
  crack: number; snap: number; tail: number; thump: number; length: number; level: number;
}
const VOICE: Record<string, Voice> = {
  // Small, dry, quick. Almost all crack and very little room.
  machine_pistol: { pitch: 1.5, sweep: 2.0, wave: 'square', crack: 1.25, snap: 0.8, tail: 0.5, thump: 0, length: 0.7, level: 0.8 },
  pdw:            { pitch: 1.35, sweep: 2.1, wave: 'square', crack: 1.2, snap: 0.85, tail: 0.6, thump: 0, length: 0.75, level: 0.85 },
  smg:            { pitch: 1.25, sweep: 2.2, wave: 'sawtooth', crack: 1.15, snap: 0.9, tail: 0.7, thump: 0, length: 0.8, level: 0.9 },
  pistol:         { pitch: 1.15, sweep: 2.4, wave: 'triangle', crack: 1.1, snap: 1.0, tail: 0.8, thump: 0, length: 0.9, level: 0.95 },
  // A revolver is a pistol with a room behind it.
  revolver:       { pitch: 0.95, sweep: 2.6, wave: 'triangle', crack: 1.0, snap: 1.2, tail: 1.3, thump: 0.3, length: 1.05, level: 1.05 },
  carbine:        { pitch: 1.1, sweep: 2.3, wave: 'sawtooth', crack: 1.1, snap: 1.0, tail: 0.9, thump: 0, length: 0.9, level: 0.95 },
  rifle:          { pitch: 1.0, sweep: 2.4, wave: 'sawtooth', crack: 1.0, snap: 1.0, tail: 1.0, thump: 0, length: 1.0, level: 1.0 },
  battle:         { pitch: 0.85, sweep: 2.5, wave: 'sawtooth', crack: 0.95, snap: 1.15, tail: 1.2, thump: 0.35, length: 1.1, level: 1.1 },
  lmg:            { pitch: 0.9, sweep: 2.2, wave: 'sawtooth', crack: 0.95, snap: 1.05, tail: 1.15, thump: 0.3, length: 1.0, level: 1.05 },
  dmr:            { pitch: 0.8, sweep: 2.8, wave: 'sawtooth', crack: 0.9, snap: 1.25, tail: 1.4, thump: 0.3, length: 1.15, level: 1.1 },
  // A long barrel: less blast, more crack, and a room that goes on.
  sniper:         { pitch: 0.7, sweep: 3.2, wave: 'sawtooth', crack: 0.8, snap: 1.5, tail: 1.9, thump: 0.45, length: 1.3, level: 1.15 },
  antimat:        { pitch: 0.55, sweep: 3.4, wave: 'square', crack: 0.7, snap: 1.6, tail: 2.2, thump: 0.9, length: 1.5, level: 1.2 },
  // Broad and low, and hardly any crack at all.
  shotgun:        { pitch: 0.65, sweep: 1.8, wave: 'triangle', crack: 0.6, snap: 0.7, tail: 1.5, thump: 0.85, length: 1.35, level: 1.15 },
  launcher:       { pitch: 0.5, sweep: 1.6, wave: 'sine', crack: 0.5, snap: 0.5, tail: 2.0, thump: 1.0, length: 1.6, level: 1.15 },
  // A string and a limb, not a charge.
  bow:            { pitch: 1.6, sweep: 1.4, wave: 'sine', crack: 1.4, snap: 0.35, tail: 0.35, thump: 0, length: 0.5, level: 0.6 },
  special:        { pitch: 1.2, sweep: 3.0, wave: 'sine', crack: 1.5, snap: 0.9, tail: 1.1, thump: 0.2, length: 1.0, level: 0.95 },
  exotic:         { pitch: 1.2, sweep: 3.0, wave: 'sine', crack: 1.5, snap: 0.9, tail: 1.1, thump: 0.2, length: 1.0, level: 0.95 },
};

export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private listener: Listener = { x: 0, y: 0, z: 0, fx: 0, fz: -1, rx: 1, rz: 0 };
  private voices = 0;
  private muted = false;

  constructor() {
    try {
      // `?mute` wins over whatever was remembered, so the game can be opened
      // silently next to something else without changing the saved setting.
      if (new URLSearchParams(location.search).has('mute')) this.muted = true;
      else this.muted = localStorage.getItem('descent.muted') === '1';
    } catch {
      // Private browsing, or storage disabled. Not a reason to be silent.
    }
  }

  get enabled(): boolean {
    return !this.muted;
  }

  /**
   * Called from the first real input.
   *
   * Autoplay policy means an audio context created before a gesture starts
   * suspended and stays that way, so this is idempotent and cheap enough to
   * call from every pointer and key handler.
   */
  start(): void {
    if (this.muted) return;
    if (!this.ctx) {
      // The headless tools run the real game loop, and the real game loop
      // reaches for a sound on every trigger pull. There is no window there.
      if (typeof window === 'undefined') return;
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.62;
      /*
       * A limiter on the end.
       *
       * A firefight is twenty voices inside a second and the sum of twenty
       * things that each peak at a fifth will clip. A gentle compressor with a
       * fast attack costs nothing and means the loud moments stay loud instead
       * of turning to fizz — which is exactly when the player needs to hear
       * what is happening.
       */
      const limiter = this.ctx.createDynamicsCompressor();
      limiter.threshold.value = -10;
      limiter.knee.value = 8;
      limiter.ratio.value = 8;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.18;
      this.master.connect(limiter).connect(this.ctx.destination);
      // One second of white noise, reused by everything that needs it. Making
      // a fresh buffer per shot is the one thing in here that would actually
      // cost something.
      const n = Math.floor(this.ctx.sampleRate);
      this.noise = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      for (let i = 0; i < n; i++) data[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  toggle(): boolean {
    this.muted = !this.muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.62;
    try {
      localStorage.setItem('descent.muted', this.muted ? '1' : '0');
    } catch { /* nothing to do about it */ }
    if (!this.muted) this.start();
    return !this.muted;
  }

  /** Where the player is and which way they are facing, in Three space. */
  setListener(x: number, y: number, z: number, yaw: number): void {
    this.listener.x = x;
    this.listener.y = y;
    this.listener.z = z;
    this.listener.fx = -Math.sin(yaw);
    this.listener.fz = -Math.cos(yaw);
    this.listener.rx = Math.cos(yaw);
    this.listener.rz = -Math.sin(yaw);
  }

  // ---------------------------------------------------------------------------
  // Placement

  /**
   * Gain and pan for a point in the world.
   *
   * Inverse-square would be right and sounds wrong: it makes everything either
   * deafening or inaudible with almost nothing in between. A gentler curve
   * with a floor keeps a distant shot audible as a distant shot, which is the
   * information the player actually wants.
   */
  private place(at?: [number, number, number]): { gain: number; pan: number } | null {
    if (!at) return { gain: 1, pan: 0 };
    const dx = at[0] - this.listener.x;
    const dy = at[1] - this.listener.y;
    const dz = at[2] - this.listener.z;
    const d = Math.hypot(dx, dy, dz);
    if (d > EARSHOT) return null;
    const gain = 1 / (1 + (d / 7) ** 1.4);
    const len = Math.hypot(dx, dz) || 1;
    const pan = Math.max(-1, Math.min(1, ((dx * this.listener.rx + dz * this.listener.rz) / len) * 0.85));
    return { gain, pan };
  }

  /** A chain of gain and pan, with the voice counter wired to its end. */
  private out(gain: number, pan: number): GainNode | null {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted) return null;
    if (this.voices >= MAX_VOICES) return null;
    this.voices++;
    const g = ctx.createGain();
    g.gain.value = gain;
    if (ctx.createStereoPanner) {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p).connect(this.master);
    } else {
      g.connect(this.master);
    }
    return g;
  }

  private done(node: AudioScheduledSourceNode, at: number): void {
    node.stop(at);
    node.onended = () => {
      this.voices = Math.max(0, this.voices - 1);
      node.disconnect();
    };
  }

  // ---------------------------------------------------------------------------
  // Primitives

  /** Filtered noise with a percussive envelope: cracks, tails, footsteps. */
  private hiss(
    dest: GainNode,
    when: number,
    seconds: number,
    filter: BiquadFilterType,
    cut: number,
    peak: number,
    q = 0.7,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.noise) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    src.playbackRate.value = 0.7 + Math.random() * 0.6;
    const bq = ctx.createBiquadFilter();
    bq.type = filter;
    bq.frequency.value = cut;
    bq.Q.value = q;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, when);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), when + 0.004);
    env.gain.exponentialRampToValueAtTime(0.0001, when + seconds);
    src.connect(bq).connect(env).connect(dest);
    src.start(when);
    this.done(src, when + seconds + 0.02);
  }

  /** A pitched tone that can sweep: bodies, whines, confirmations. */
  private tone(
    dest: GainNode,
    when: number,
    seconds: number,
    from: number,
    to: number,
    peak: number,
    type: OscillatorType = 'sine',
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(20, from), when);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), when + seconds);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, when);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), when + Math.min(0.012, seconds * 0.2));
    env.gain.exponentialRampToValueAtTime(0.0001, when + seconds);
    osc.connect(env).connect(dest);
    osc.start(when);
    this.done(osc, when + seconds + 0.02);
  }

  // ---------------------------------------------------------------------------
  // The game's own sounds

  /**
   * A gunshot, from the gun's own numbers.
   *
   * `heft` is the whole character: it is what a shot's energy is, and it sets
   * how low the body sits, how long the tail runs and how much crack there is
   * on top. A pistol and an anti-materiel rifle are the same three layers in
   * different proportions, which is also true of the real thing.
   */
  shot(w: Weapon, at?: [number, number, number]): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const heft = Math.max(0.25, Math.min(5, (w.damage * w.pellets) / 28));
    const quiet = SUPPRESSED.test(w.name) || SUPPRESSED.test(w.model ?? '');
    const v = VOICE[w.cls] ?? VOICE.rifle;
    /*
     * Two guns of a class are not the same gun.
     *
     * A deterministic wobble off the weapon's own id, so a Galil and an AKM
     * are recognisably the same family and audibly different instruments —
     * and the same one sounds the same every time you pick it up. Small: this
     * is the difference between two barrels, not between two calibres.
     */
    let h = 2166136261;
    for (let i = 0; i < w.id.length; i++) { h ^= w.id.charCodeAt(i); h = Math.imul(h, 16777619); }
    const detune = 0.88 + ((h >>> 9) & 255) / 255 * 0.24;

    const g = this.out(spot.gain * (quiet ? 0.4 : 0.9) * v.level, spot.pan);
    if (!g) return;
    const t = ctx.currentTime;

    // Body: the muzzle blast, falling as it goes.
    const body = (190 / Math.sqrt(heft)) * v.pitch * detune;
    this.tone(g, t, (0.06 + heft * 0.02) * v.length, body * v.sweep, body * 0.55, 0.48, v.wave);
    /*
     * And a second, lower body on the ones that thump.
     *
     * A shotgun and an anti-materiel rifle are not loud versions of a pistol;
     * what makes them read is a component an octave down that arrives with the
     * first and outlives it. Nothing else gets one, which is what keeps them
     * distinct rather than everything being big.
     */
    if (v.thump > 0) {
      this.tone(g, t, (0.09 + heft * 0.04) * v.length, body * 0.5, body * 0.22, 0.48 * v.thump, 'sine');
    }
    if (!quiet) {
      // Crack: the snap, and the only part that carries across a map.
      this.hiss(g, t, (0.022 + heft * 0.004) * v.length, 'highpass', (2400 + 900 / heft) * v.crack, 0.5 * v.snap);
      // Tail: the room answering. Longer for bigger, shorter for faster.
      const tail = Math.min(0.5, (0.08 + heft * 0.08 + Math.min(0.18, w.interval * 0.35)) * v.tail);
      this.hiss(g, t + 0.01, tail, 'lowpass', 900 + 500 / heft, 0.22, 0.9);
    } else {
      this.hiss(g, t, 0.05, 'bandpass', 700 * v.pitch, 0.3, 2.4);
    }
  }

  /** The click of a trigger on an empty chamber. */
  dry(): void {
    const g = this.out(0.5, 0);
    if (!g || !this.ctx) return;
    this.hiss(g, this.ctx.currentTime, 0.03, 'bandpass', 2600, 1.6, 3);
  }

  /** Working the action, or a magazine going in. */
  clack(pitch = 1, level = 0.4): void {
    const g = this.out(level, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.hiss(g, t, 0.035, 'bandpass', 1500 * pitch, 1.7, 2.2);
    this.tone(g, t, 0.05, 260 * pitch, 120 * pitch, 0.5, 'square');
  }

  /** A round finding a body. Two ticks for a head, so it reads without the HUD. */
  hit(head: boolean): void {
    const g = this.out(0.7, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(g, t, 0.05, head ? 1900 : 1250, head ? 1400 : 900, 0.55, 'square');
    if (head) this.tone(g, t + 0.055, 0.05, 2500, 1900, 0.5, 'square');
  }

  /** Something dying, at a pitch that says how big it was. */
  kill(t: EnemyType, at: [number, number, number]): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const g = this.out(spot.gain * 0.8, spot.pan);
    if (!g) return;
    const now = ctx.currentTime;
    const size = Math.max(0.4, Math.min(3, t.health / 40));
    this.tone(g, now, 0.24 + size * 0.06, 420 / size, 90 / size, 0.85, 'sawtooth');
    this.hiss(g, now, 0.2, 'lowpass', 1400, 0.7);
  }

  /** Something shooting at you, and it should not sound like your own gun. */
  enemyShot(t: EnemyType, at: [number, number, number]): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const g = this.out(spot.gain * 0.7, spot.pan);
    if (!g) return;
    const now = ctx.currentTime;
    const pitch = 240 + (2.2 - Math.min(2.2, t.height)) * 260;
    this.tone(g, now, 0.13, pitch * 2.4, pitch * 0.7, 0.95, 'sawtooth');
    this.hiss(g, now, 0.07, 'bandpass', pitch * 5, 0.5, 1.6);
  }

  /**
   * Something arriving.
   *
   * Quiet, and placed, because its whole job is to tell you which direction to
   * look before the thing that arrived is a problem. A falling pair of tones
   * an octave apart, which is nothing else in the mix.
   */
  spawned(at: [number, number, number]): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const g = this.out(spot.gain * 0.5, spot.pan);
    if (!g) return;
    const now = ctx.currentTime;
    this.tone(g, now, 0.5, 1180, 620, 0.5, 'triangle');
    this.tone(g, now + 0.04, 0.44, 590, 310, 0.35, 'sine');
  }

  /**
   * The wind-up.
   *
   * The one cue that has to be heard through everything else, because it is
   * the only warning for the shots that take a third of your health. A rising
   * tone, because a rise is the shape people already read as "about to".
   */
  telegraph(seconds: number, at: [number, number, number], lobbed: boolean): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const g = this.out(Math.max(0.35, spot.gain) * 0.7, spot.pan);
    if (!g) return;
    const now = ctx.currentTime;
    if (lobbed) {
      this.tone(g, now, seconds, 300, 620, 1.3, 'triangle');
      this.tone(g, now, seconds, 302, 626, 1.0, 'triangle');
    } else {
      this.tone(g, now, seconds, 520, 1450, 1.3, 'sawtooth');
      this.tone(g, now, seconds, 524, 1462, 0.8, 'triangle');
    }
  }

  /** Taking damage: felt low, in the chest. */
  hurt(fraction: number): void {
    const g = this.out(Math.min(1, 0.5 + fraction * 2), 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(g, t, 0.22, 180, 60, 0.6, 'sine');
    this.hiss(g, t, 0.14, 'lowpass', 600, 0.35);
  }

  /**
   * Walking on something.
   *
   * A footfall is broadband and mostly low — a scuff and a thud together. The
   * first version used a narrow band-pass, which is what a footstep looks like
   * on a spectrogram and not at all what one sounds like: it passed so little
   * energy that the sound was inaudible next to everything else.
   */
  step(own: boolean, at?: [number, number, number]): void {
    const spot = this.place(at);
    if (!spot || !this.ctx) return;
    const g = this.out(spot.gain * (own ? 0.22 : 0.75), spot.pan);
    if (!g) return;
    const t = this.ctx.currentTime;
    this.hiss(g, t, 0.06, 'lowpass', 700 + Math.random() * 400, 1.5, 0.8);
    this.tone(g, t, 0.05, 120, 62, 0.75, 'sine');
  }

  /** Picking something up: two notes, rising, so it reads as a gain. */
  pickup(kind: string): void {
    const g = this.out(0.6, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    const base = kind === 'health' ? 520 : kind === 'ammo' ? 420 : kind === 'weapon' ? 340 : 460;
    this.tone(g, t, 0.09, base, base, 0.5, 'triangle');
    this.tone(g, t + 0.075, 0.14, base * 1.5, base * 1.5, 0.5, 'triangle');
  }

  /** The floor is clear and the way out is open. A chord, held. */
  /**
   * The marked target is on the floor with you.
   *
   * Lower and slower than an ordinary arrival, and heard from wherever it is
   * rather than at your ear: the point is that you know something has entered
   * the map and roughly where, not that something is about to hit you.
   */
  spotted(at: [number, number, number]): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const g = this.out(Math.max(0.34, spot.gain) * 0.8, spot.pan);
    if (!g) return;
    const now = ctx.currentTime;
    this.tone(g, now, 0.85, 196, 174, 0.5, 'triangle');
    this.tone(g, now + 0.16, 0.8, 131, 116, 0.44, 'sine');
  }

  /**
   * The signal is reading. One note, repeating faster as it fills.
   *
   * Given a pitch rather than a volume so it can be heard under gunfire: a
   * rising tone is audible against noise in a way that a louder one is not.
   */
  signal(progress: number): void {
    const g = this.out(0.3, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    const f = 330 + progress * 330;
    this.tone(g, t, 0.16, f, f, 0.5, 'sine');
  }

  /**
   * Something of yours going off.
   *
   * Distinct from an enemy's detonation: lower, shorter and with a snap on the
   * front, so an explosion you caused and one that is about to hurt you are
   * never confused in the middle of a fight.
   */
  blast(at: [number, number, number]): void {
    const ctx = this.ctx;
    const spot = this.place(at);
    if (!ctx || !spot) return;
    const g = this.out(spot.gain * 0.9, spot.pan);
    if (!g) return;
    const now = ctx.currentTime;
    this.hiss(g, now, 0.26, 'lowpass', 900, 0.7);
    this.tone(g, now, 0.3, 128, 46, 0.7, 'sine');
  }

  cleared(): void {
    const g = this.out(0.55, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    for (const [i, f] of [392, 523, 659].entries()) {
      this.tone(g, t + i * 0.07, 0.5 - i * 0.06, f, f, 0.62, 'triangle');
    }
  }

  /** The run is over. The same shape, downward. */
  died(): void {
    const g = this.out(0.6, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    for (const [i, f] of [523, 392, 262].entries()) {
      this.tone(g, t + i * 0.13, 0.7 - i * 0.1, f, f * 0.98, 0.42, 'sine');
    }
  }

  /**
   * A heartbeat, for the last third.
   *
   * Two thumps, low, quiet enough to sit under a firefight and impossible to
   * miss in a quiet corridor — which is exactly when it matters, because the
   * decision it is asking for is whether to go and find a medkit.
   */
  heartbeat(urgency: number): void {
    const g = this.out(0.5 + urgency * 0.4, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(g, t, 0.16, 78, 44, 0.9, 'sine');
    this.tone(g, t + 0.19, 0.13, 66, 38, 0.6, 'sine');
  }

  // ---------------------------------------------------------------------------
  // The bed
  //
  // A room is never silent, and a game that is silent between fights feels
  // like a demo of a game. This is one noise source and one drone, running for
  // the whole session and re-tuned when a floor changes: an open desert map
  // gets wind, a plant gets a hum, a night interior gets room tone. It sits at
  // the very bottom of the mix — you should notice it when it stops rather
  // than when it starts.

  private bed: { noise: AudioBufferSourceNode; filter: BiquadFilterNode; gain: GainNode; drone: OscillatorNode; droneGain: GainNode } | null = null;

  /**
   * Tune the bed to a floor.
   *
   * `openness` is how much of the map is sky — wind rather than room tone —
   * and `machinery` is how industrial it is. Both come from the map's own
   * architecture, so a foundry and a market do not sound the same.
   */
  ambience(openness: number, machinery: number, night: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.noise || this.muted) return;
    if (!this.bed) {
      const noise = ctx.createBufferSource();
      noise.buffer = this.noise;
      noise.loop = true;
      noise.playbackRate.value = 0.22;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 400;
      filter.Q.value = 0.5;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      noise.connect(filter).connect(gain).connect(this.master);
      noise.start();

      const drone = ctx.createOscillator();
      drone.type = 'sine';
      drone.frequency.value = 56;
      const droneGain = ctx.createGain();
      droneGain.gain.value = 0;
      drone.connect(droneGain).connect(this.master);
      drone.start();

      this.bed = { noise, filter, gain, drone, droneGain };
    }
    const b = this.bed;
    const t = ctx.currentTime;
    // Two seconds to cross-fade, so arriving somewhere is a change of air
    // rather than a cut.
    b.filter.frequency.setTargetAtTime(260 + openness * 1500, t, 0.6);
    b.gain.gain.setTargetAtTime(0.02 + openness * 0.05, t, 0.9);
    b.drone.frequency.setTargetAtTime(40 + machinery * 44, t, 0.9);
    b.droneGain.gain.setTargetAtTime(machinery * (night ? 0.05 : 0.03), t, 0.9);
  }

  /**
   * The way out, heard rather than read.
   *
   * A soft two-note drone from wherever the exit is, panned and attenuated
   * like anything else. The arrow already says which way and how far, so this
   * is not navigation — it is the floor changing character the moment it is
   * clear, which is the one beat in a run that deserves one.
   *
   * Called every frame while the exit exists and with `null` when it does not.
   */
  private beaconNodes: { a: OscillatorNode; b: OscillatorNode; gain: GainNode; pan: StereoPannerNode | null } | null = null;

  beacon(at: [number, number, number] | null): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted) return;
    if (!at) {
      if (this.beaconNodes) this.beaconNodes.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
      return;
    }
    if (!this.beaconNodes) {
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
      if (pan) gain.connect(pan).connect(this.master);
      else gain.connect(this.master);
      const a = ctx.createOscillator();
      a.type = 'sine';
      a.frequency.value = 196;
      const b = ctx.createOscillator();
      b.type = 'sine';
      b.frequency.value = 294;
      a.connect(gain);
      b.connect(gain);
      a.start();
      b.start();
      this.beaconNodes = { a, b, gain, pan };
    }
    const spot = this.place(at);
    const n = this.beaconNodes;
    const t = ctx.currentTime;
    n.gain.gain.setTargetAtTime(spot ? spot.gain * 0.09 : 0, t, 0.15);
    if (n.pan && spot) n.pan.pan.setTargetAtTime(spot.pan, t, 0.12);
  }

  /** Silence the bed — between runs, or while a screen is up. */
  hush(): void {
    if (!this.ctx) return;
    this.beacon(null);
    if (!this.bed) return;
    this.bed.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.25);
    this.bed.droneGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.25);
  }

  /** Arriving somewhere new. */
  descend(): void {
    const g = this.out(0.5, 0);
    if (!g || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(g, t, 0.9, 160, 70, 0.3, 'sine');
    this.hiss(g, t, 0.5, 'lowpass', 400, 0.2, 0.6);
  }
}

/** Names that mean the gun was built not to be heard. */
const SUPPRESSED = /silent|suppress|welrod|vss|vintorez|de ?lisle|mp5 ?sd|sd\b|\bpb\b|\bpss\b|vsk|vykhlop|whisper|hush|sotto/i;

/** One for the whole game. Two audio contexts is one too many on a phone. */
export const sound = new Sound();
